// Central, build-time-configurable definition of the Training SPA content links.
//
// Each module key maps to the business-managed Articulate content shown to the
// user before its quiz. URLs default to the current content and can be
// overridden per environment via REACT_APP_* env vars, so the business can
// change the linked content without a code change.

const COMPLIANCE_AU_URL =
  process.env.REACT_APP_TRAINING_LINK_COMPLIANCE_AU ||
  'https://rise.articulate.com/share/qkEVXZvqrvjPHrL7b2UwxaIWEotNTyLY';
const COMPLIANCE_NZ_URL =
  process.env.REACT_APP_TRAINING_LINK_COMPLIANCE_NZ || COMPLIANCE_AU_URL;
const PARTNER_HUB_URL = process.env.REACT_APP_TRAINING_LINK_PARTNER_HUB || '';
const ASSISTED_APPLY_URL = process.env.REACT_APP_TRAINING_LINK_ASSISTED_APPLY || '';

const COMPLIANCE_LINK_TITLE = 'Product & Compliance Training';
const COMPLIANCE_LINK_BODY =
  'The following link provides the core training you need before discussing a ' +
  'participating Latitude credit card with a customer. It covers what the product ' +
  'is and how it works, your responsibilities when offering Interest Free plans, ' +
  'key compliance requirements and the customer standards you must follow.';

export const TRAINING_MODULES = {
  'compliance-AU': {
    key: 'compliance-AU',
    region: 'AU',
    linkTitle: COMPLIANCE_LINK_TITLE,
    linkBody: COMPLIANCE_LINK_BODY,
    linkButtonLabel: 'Start Product & Compliance Training',
    contentUrl: COMPLIANCE_AU_URL,
    // Quiz is revealed only after both the Compliance and Partner Hub links are opened.
    requiredClicks: ['compliance-AU', 'partnerHub'],
  },
  'compliance-NZ': {
    key: 'compliance-NZ',
    region: 'NZ',
    linkTitle: COMPLIANCE_LINK_TITLE,
    linkBody: COMPLIANCE_LINK_BODY,
    linkButtonLabel: 'Start Product & Compliance Training',
    contentUrl: COMPLIANCE_NZ_URL,
    requiredClicks: ['compliance-NZ', 'partnerHub'],
  },
  partnerHub: {
    key: 'partnerHub',
    linkTitle: 'Partner Hub Process Training',
    linkBody:
      'The following link provides training on how to use Partner Hub and the key ' +
      'processes you need to follow when completing transactions.',
    linkButtonLabel: 'Start Partner Hub Training',
    contentUrl: PARTNER_HUB_URL,
    // Partner Hub is link-only; its pass is covered by the compliance quiz.
    linkOnly: true,
  },
  assistedApply: {
    key: 'assistedApply',
    linkTitle: 'Assisted Application training',
    linkBody:
      'The following link accesses the Assisted Application training. Please ' +
      'review this content before completing the Assisted Application questionnaire.',
    linkButtonLabel: 'Read the Assisted Application training',
    contentUrl: ASSISTED_APPLY_URL,
    requiresAssistedApply: true,
    requiredClicks: ['assistedApply'],
  },
};

// Legacy module key used by existing links/bookmarks; defaults to AU compliance.
TRAINING_MODULES.training1 = TRAINING_MODULES['compliance-AU'];

export function getTrainingModule(moduleName) {
  return TRAINING_MODULES[moduleName] || TRAINING_MODULES.training1;
}

// resolveModule maps a requested module to the effective module for the user's
// region. A generic/legacy compliance request resolves to the region-specific
// compliance module (compliance-AU / compliance-NZ); other modules pass through.
export function resolveModule(moduleName, region) {
  const normalizedRegion = (region || '').toUpperCase();
  const isGenericCompliance =
    !moduleName || moduleName === 'training1' || moduleName === 'compliance';

  if (isGenericCompliance) {
    if (normalizedRegion === 'AU' || normalizedRegion === 'NZ') {
      return `compliance-${normalizedRegion}`;
    }
    return 'training1';
  }

  return moduleName;
}

// getRequiredLinks returns the ordered list of training links the user must open
// before the module's quiz is revealed (design T2: the compliance quiz needs the
// Compliance + Partner Hub links, the Assisted Apply quiz needs the AA link).
// Links without a configured contentUrl are skipped so an unconfigured link can
// never permanently block the quiz.
export function getRequiredLinks(moduleName) {
  const trainingModule = getTrainingModule(moduleName);
  const keys = trainingModule.requiredClicks || [trainingModule.key];
  return keys
    .map((key) => TRAINING_MODULES[key])
    .filter((link) => link && link.contentUrl);
}
